import { useState } from 'react';
import {
  Modal, Tag, Button, EntityAvatar, Popconfirm, CodeChip, cn,
} from '../../ui';
import type { TagTone } from '../../ui';
import {
  Package, Check, X, Pencil, ArrowDownToLine, ArrowUpFromLine, Warehouse as WarehouseIcon,
  Clock, FileText, StickyNote, CalendarDays,
} from 'lucide-react';
import { RoleBadge, roleLabel } from '../../components/RoleBadge';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { CopyValue } from '../../components/CopyValue';
import { productImage } from '../../lib/imageUrls';
import { fmtQty } from '../../lib/format';
import { formatIst } from '../../lib/time';
import { reasonLabel } from './AdjustmentFormModal';

const STATUS_TONE: Record<string, TagTone> = { DRAFT: 'neutral', SUBMITTED: 'gold', APPROVED: 'green', REJECTED: 'red' };

/** What each state MEANS for stock — the sentence the tag on its own can't say. */
const STATUS_LINE: Record<string, string> = {
  DRAFT: 'Not submitted yet — nothing has moved.',
  SUBMITTED: 'Waiting on an approver. Book stock is unchanged until it is decided.',
  APPROVED: 'Posted to the ledger — warehouse stock has changed.',
  REJECTED: 'Turned down. No ledger entry was ever posted.',
};

/** One labelled fact. SECTION / label / value stay three visible levels. */
function OField({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="type-label text-muted">{label}</div>
      <div className="mt-0.5 min-w-0 type-body text-content">{children}</div>
    </div>
  );
}

function Section({ title, icon, children, className }: {
  title: string; icon?: React.ReactNode; children: React.ReactNode; className?: string;
}) {
  return (
    <div className={cn('rounded-card border border-line bg-surface p-3', className)}>
      {/* `flex`, not `inline-flex`: an inline title does not end the line, so a section whose body is
          inline content rendered as "EVIDENCENothing attached". */}
      <div className="mb-2 flex items-center gap-1.5 type-overline text-muted">
        {icon && <span className="text-brand-600 dark:text-brand-400">{icon}</span>}
        {title}
      </div>
      {children}
    </div>
  );
}

/** A named act with the person who performed it, their role, and when. */
function ActorLine({ label, name, role, at, tone }: {
  label: string; name?: string | null; role?: string | null; at?: string | null; tone: 'raised' | 'decided';
}) {
  return (
    <div className={cn(
      'flex items-start gap-2.5 rounded-control border p-2.5',
      tone === 'raised' ? 'border-sky-500/25 bg-sky-500/[0.06]' : 'border-brand-500/25 bg-brand-500/[0.06]',
    )}>
      <EntityAvatar name={name ?? '—'} size={32} />
      <div className="min-w-0 flex-1">
        <div className="type-label text-muted">{label}</div>
        <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="truncate type-body-strong text-content">{name ?? 'not recorded'}</span>
          {/* A pill that repeats the name says nothing twice — see the seeded "Super Admin" user. */}
          {roleLabel(role ?? '') !== (name ?? '') && <RoleBadge role={role} />}
        </div>
        {at && <div className="mt-0.5 type-caption text-subtle">{formatIst(at)}</div>}
      </div>
    </div>
  );
}

/**
 * THE WHOLE RECORD OF ONE CORRECTION.
 *
 * The register can only ever show a row's worth; this is where the rest of it lives — what moved,
 * out of which lot, why, who raised it, who decided it, what evidence they attached and what the
 * state means for stock right now. Colour carries the direction (stock in vs stock out) and the
 * decision, so the shape of the record is readable before a word of it is.
 */
export function AdjustmentDetailModal({
  row, product, warehouse, canApprove, canCreate, approving, onEdit, onApprove, onReject, onClose,
}: {
  row: any;
  product?: { name?: string; sku?: string; variantLabel?: string } & Record<string, any>;
  warehouse: string;
  canApprove: boolean;
  canCreate: boolean;
  approving: boolean;
  onEdit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onClose: () => void;
}) {
  const isIn = row.direction === 'IN';
  const pending = row.status === 'SUBMITTED';
  const evidence = [row.evidenceUrl].filter(Boolean) as string[];
  const productImg = product ? productImage(product) : undefined;

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={720}
      title={
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate">Adjustment {row.adjustmentNo}</span>
          <Tag tone={STATUS_TONE[row.status]}>{row.status}</Tag>
        </span>
      }
      footer={
        <>
          <Button variant="default" onClick={onClose}>Close</Button>
          {pending && canCreate && (
            <Button variant="soft-info" icon={<Pencil className="size-4" />} onClick={onEdit}>Edit</Button>
          )}
          {pending && canApprove && (
            <>
              <Button variant="soft-danger" icon={<X className="size-4" />} onClick={onReject}>Reject</Button>
              <Popconfirm
                title="Approve adjustment?"
                description="This posts the stock movement to the ledger and cannot be undone."
                okText="Approve"
                onConfirm={onApprove}
              >
                <Button variant="primary" icon={<Check className="size-4" />} loading={approving}>Approve</Button>
              </Popconfirm>
            </>
          )}
        </>
      }
    >
      <div className="space-y-3">
        {/* THE MOVEMENT — the one thing a reader opens this for, in the direction's own colour. */}
        <div className={cn(
          'flex flex-wrap items-center gap-3 rounded-card border p-3',
          isIn ? 'border-brand-500/30 bg-brand-500/[0.07]' : 'border-danger-500/30 bg-danger-500/[0.06]',
        )}>
          <span className={cn(
            'grid size-11 shrink-0 place-items-center rounded-control',
            isIn ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300' : 'bg-danger-500/12 text-danger-600 dark:text-danger-400',
          )}>
            {isIn ? <ArrowDownToLine className="size-5" /> : <ArrowUpFromLine className="size-5" />}
          </span>
          <div className="min-w-0">
            <div className="type-label text-muted">{isIn ? 'Added back to stock' : 'Taken out of stock'}</div>
            <div className="type-metric text-content">
              {isIn ? '+' : '−'}{fmtQty(row.qty)}
            </div>
          </div>
          <div className="ms-auto flex min-w-0 items-center gap-2.5">
            {/* The pack shot is a THUMBNAIL YOU CAN OPEN, not decoration. */}
            {productImg
              ? <AttachmentCell files={[productImg]} label={product?.name ?? 'Product'} size={44} />
              : <EntityAvatar name={product?.name ?? '?'} square size={44} icon={<Package className="size-4" />} />}
            <div className="min-w-0">
              <div className="truncate type-body-strong text-content">{product?.name ?? `#${row.productId}`}</div>
              <div className="truncate type-caption text-subtle">
                {product?.sku ?? ''}{product?.variantLabel ? ` · ${product.variantLabel}` : ''}
              </div>
            </div>
          </div>
        </div>

        {/* WHAT THE STATE MEANS FOR STOCK — a tag alone never says this. */}
        <div className={cn(
          'rounded-card border p-2.5 type-caption',
          row.status === 'APPROVED' ? 'border-brand-500/25 bg-brand-500/[0.06] text-content'
            : row.status === 'REJECTED' ? 'border-danger-500/25 bg-danger-500/[0.05] text-content'
              : 'border-harvest-400/40 bg-harvest-400/[0.10] text-content',
        )}>
          {STATUS_LINE[row.status] ?? ''}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Section title="Where and what" icon={<WarehouseIcon className="size-3" />}>
            <div className="grid gap-2.5">
              <OField label="Warehouse">{warehouse}</OField>
              <OField label="Batch">
                {row.batchNo
                  ? <CopyValue value={row.batchNo} label="Batch no"><CodeChip>{row.batchNo}</CodeChip></CopyValue>
                  : row.batchId
                    ? <span className="font-mono text-subtle">#{row.batchId}</span>
                    : <span className="text-subtle">
                        {isIn ? 'a dedicated ADJ- lot was created' : 'oldest lots first (FIFO)'}
                      </span>}
              </OField>
              <OField label="Reason">
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-2 shrink-0 rounded-pill bg-brand-500" />
                  {reasonLabel(row.reason)}
                </span>
              </OField>
            </div>
          </Section>

          <Section title="Paper trail" icon={<CalendarDays className="size-3" />}>
            <div className="grid gap-2.5">
              <OField label="Adjustment no">
                <CopyValue value={row.adjustmentNo} label="Adjustment no">
                  <span className="type-body-strong">{row.adjustmentNo}</span>
                </CopyValue>
              </OField>
              <OField label="Raised">{row.createdAt ? formatIst(row.createdAt) : '—'}</OField>
              <OField label="Last change">{row.updatedAt ? formatIst(row.updatedAt) : '—'}</OField>
            </div>
          </Section>
        </div>

        {/* WHO DID WHAT — the two named acts, each in its own colour. */}
        <Section title="Who did what" icon={<Clock className="size-3" />}>
          <div className="grid gap-2 sm:grid-cols-2">
            <ActorLine label="Raised by" name={row.raisedByName} role={row.raisedByRole} at={row.createdAt} tone="raised" />
            {row.status === 'APPROVED' || row.status === 'REJECTED' ? (
              <ActorLine
                label={row.status === 'APPROVED' ? 'Approved by' : 'Rejected by'}
                name={row.decidedByName} role={row.decidedByRole} at={row.updatedAt} tone="decided"
              />
            ) : (
              <div className="flex items-center gap-2.5 rounded-control border border-dashed border-line p-2.5">
                <span className="grid size-8 shrink-0 place-items-center rounded-pill bg-harvest-400/20 text-harvest-700 dark:text-harvest-300">
                  <Clock className="size-4" />
                </span>
                <div className="min-w-0">
                  <div className="type-label text-muted">Decision</div>
                  <div className="type-body text-subtle">nobody has decided this yet</div>
                </div>
              </div>
            )}
          </div>
        </Section>

        <div className="grid gap-3 sm:grid-cols-2">
          <Section title="Evidence" icon={<FileText className="size-3" />}>
            {evidence.length
              ? <AttachmentCell files={evidence} label={`Evidence for ${row.adjustmentNo}`} size={56} />
              : <span className="type-caption text-subtle">
                  Nothing attached{['DAMAGE', 'EXPIRY', 'THEFT'].includes(row.reason) ? ' — auditors look for it on this reason' : ''}
                </span>}
          </Section>
          <Section title="Notes" icon={<StickyNote className="size-3" />}>
            {row.notes
              ? <p className="whitespace-pre-wrap type-body text-content">{row.notes}</p>
              : <span className="type-caption text-subtle">No note was left</span>}
          </Section>
        </div>
      </div>
    </Modal>
  );
}
